import React, { useState } from "react";
import { FormControl, FormLabel, Select, MenuItem, TextField, Checkbox, FormControlLabel, Button, IconButton, Box } from "@mui/material";
import { useNode, useEditor } from "@craftjs/core";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { v4 as uuidv4 } from 'uuid';
import { CardCraft } from './CardCraft';

export const TabContainer = ({
    name,
    hidden,
    style,
    tabs = [
        { id: uuidv4(), label: "Tab 1", documentId: "" },
        { id: uuidv4(), label: "Tab 2", documentId: "" }
    ],
    activeTab = 0,
    tabSize = "",
    tabStyle = "",
    fullWidth = false,
    centered = false,
    boxed = false,
    toggle = false,
    rounded = false,
    children,
    ...props
}) => {
    const [currentActiveTab, setCurrentActiveTab] = useState(activeTab);
    
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered,
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    if (hidden) return null;

    const getSelectionStyle = () => {
        if (!enabled) return {};

        if (isSelected) {
            return {
                outline: '1px dashed #2196f3',
                outlineOffset: '2px'
            };
        }
        if (isHovered) {
            return {
                outline: '1px dashed #ff9800',
                outlineOffset: '1px'
            };
        }
        return {};
    };

    const handleTabClick = (index) => {
        setCurrentActiveTab(index);
    };

    // Use local state always

    const buildTabClasses = () => {
        const classes = ['tabs'];
        if (tabSize) classes.push(tabSize);
        if (tabStyle) classes.push(tabStyle);
        if (fullWidth) classes.push('is-fullwidth');
        if (centered) classes.push('is-centered');
        if (boxed) classes.push('is-boxed');
        if (toggle) classes.push('is-toggle');
        if (rounded) classes.push('is-toggle-rounded');
        return classes.join(' ');
    };

    return (
        <div
            ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
            name={name}
            className={enabled ? '' : 'tab-container-disabled'}
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                ...style,
                ...getSelectionStyle()
            }}
            {...props}
        >
            <div className={buildTabClasses()}>
                <ul>
                    {tabs.map((tab, index) => (
                        <li
                            key={tab.id}
                            className={currentActiveTab === index ? 'is-active' : ''}
                            onClick={() => handleTabClick(index)}
                            style={{ cursor: 'pointer' }}
                        >
                            <a>{tab.label}</a>
                        </li>
                    ))}
                </ul>
            </div>

            <div
                style={{
                    flex: 1,
                    minHeight: enabled ? '100px' : 'auto',
                    position: 'relative'
                }}
            >
                {tabs.map((tab, index) => (
                    <div
                        key={tab.id}
                        style={{
                            visibility: currentActiveTab === index ? 'visible' : 'hidden',
                            position: currentActiveTab === index ? 'relative' : 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: currentActiveTab === index ? 1 : 0
                        }}
                    >
                        <CardCraft
                            documentId={tab.documentId}
                            qwantumId="card"
                            name={`${name} - ${tab.label}`}
                            width="100%"
                            height="100%"
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

// Settings component remains the same
export const TabContainerSettings = () => {
    const {
        name,
        tabs,
        activeTab,
        tabSize,
        tabStyle,
        fullWidth,
        centered,
        boxed,
        toggle,
        rounded,
        hidden,
        actions: { setProp }
    } = useNode(node => ({
        name: node.data.props.name,
        tabs: node.data.props.tabs,
        activeTab: node.data.props.activeTab,
        tabSize: node.data.props.tabSize,
        tabStyle: node.data.props.tabStyle,
        fullWidth: node.data.props.fullWidth,
        centered: node.data.props.centered,
        boxed: node.data.props.boxed,
        toggle: node.data.props.toggle,
        rounded: node.data.props.rounded,
        hidden: node.data.props.hidden
    }));

    const addTab = () => {
        setProp(props => {
            props.tabs = [...props.tabs, { id: uuidv4(), label: `Tab ${props.tabs.length + 1}`, documentId: "" }];
        });
    };

    const removeTab = (index) => {
        setProp(props => {
            if (props.tabs.length > 1) {
                props.tabs = props.tabs.filter((_, i) => i !== index);
                if (props.activeTab >= props.tabs.length - 1) {
                    props.activeTab = Math.max(0, props.tabs.length - 2);
                }
            }
        });
    };

    const updateTabLabel = (index, label) => {
        setProp(props => {
            props.tabs[index].label = label;
        });
    };

    const updateTabDocumentId = (index, documentId) => {
        setProp(props => {
            props.tabs[index].documentId = documentId;
        });
    };

    const setActiveTab = (index) => {
        setProp(props => props.activeTab = index);
    };

    return (
        <div>
            <FormControl fullWidth margin="normal">
                <FormLabel>Component Name</FormLabel>
                <TextField
                    value={name || ''}
                    onChange={(e) => setProp(props => props.name = e.target.value)}
                    size="small"
                    placeholder="Enter component name"
                />
            </FormControl>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={hidden || false}
                        onChange={(e) => setProp(props => props.hidden = e.target.checked)}
                    />
                }
                label="Hidden"
            />

            <FormControl fullWidth margin="normal">
                <FormLabel>Tabs (Click tab number to make it active)</FormLabel>
                <Box>
                    {tabs?.map((tab, index) => (
                        <Box key={tab.id} display="flex" alignItems="center" gap={1} marginBottom={1}>
                            <Button
                                variant={activeTab === index ? "contained" : "outlined"}
                                size="small"
                                onClick={() => setActiveTab(index)}
                                style={{ minWidth: '60px' }}
                            >
                                {index + 1}
                            </Button>
                            <TextField
                                size="small"
                                placeholder="Tab Label"
                                value={tab.label}
                                onChange={(e) => updateTabLabel(index, e.target.value)}
                                style={{ flex: 1 }}
                            />
                            <TextField
                                size="small"
                                placeholder="Document ID"
                                value={tab.documentId || ''}
                                onChange={(e) => updateTabDocumentId(index, e.target.value)}
                                style={{ flex: 1, marginLeft: '8px' }}
                            />
                            <IconButton
                                size="small"
                                onClick={() => removeTab(index)}
                                disabled={tabs.length <= 1}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        startIcon={<AddIcon />}
                        onClick={addTab}
                        size="small"
                        variant="outlined"
                    >
                        Add Tab
                    </Button>
                </Box>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Tab Size</FormLabel>
                <Select
                    value={tabSize || ''}
                    onChange={(e) => setProp(props => props.tabSize = e.target.value)}
                    size="small"
                >
                    <MenuItem value="">Normal</MenuItem>
                    <MenuItem value="is-small">Small</MenuItem>
                    <MenuItem value="is-medium">Medium</MenuItem>
                    <MenuItem value="is-large">Large</MenuItem>
                </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Tab Style</FormLabel>
                <Select
                    value={tabStyle || ''}
                    onChange={(e) => setProp(props => props.tabStyle = e.target.value)}
                    size="small"
                >
                    <MenuItem value="">Default</MenuItem>
                    <MenuItem value="is-right">Right Aligned</MenuItem>
                </Select>
            </FormControl>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={fullWidth}
                        onChange={(e) => setProp(props => props.fullWidth = e.target.checked)}
                    />
                }
                label="Full Width"
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={centered}
                        onChange={(e) => setProp(props => props.centered = e.target.checked)}
                    />
                }
                label="Centered"
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={boxed}
                        onChange={(e) => setProp(props => props.boxed = e.target.checked)}
                    />
                }
                label="Boxed Style"
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={toggle}
                        onChange={(e) => setProp(props => props.toggle = e.target.checked)}
                    />
                }
                label="Toggle Style"
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={rounded}
                        onChange={(e) => setProp(props => props.rounded = e.target.checked)}
                    />
                }
                label="Rounded (requires Toggle)"
            />
        </div>
    );
};

export const TabContainerDefaultProps = {
    name: "TabContainer",
    tabs: [
        { id: uuidv4(), label: "Tab 1", documentId: "" },
        { id: uuidv4(), label: "Tab 2", documentId: "" }
    ],
    activeTab: 0,
    tabSize: "",
    tabStyle: "",
    fullWidth: false,
    centered: false,
    boxed: false,
    toggle: false,
    rounded: false,
    hidden: false
};

TabContainer.craft = {
    props: TabContainerDefaultProps,
    related: {
        settings: TabContainerSettings
    },
    rules: {
        canDrag: () => true,
        canDrop: () => true,
        canMoveIn: () => true
    },
    isCanvas: false,
    isCanvas: true,
    displayName: 'Tab Container'
};